// src/components/OnboardingModal.jsx
// Shown once after first login (onboarding_done=false).
// Step 1: Which sports?   Step 2: City / country
// Roles are handled by RolePickerModal — this modal no longer asks "who are you"
// (that was a duplicate role picker). Saves preferences to /api/preferences.
import React, { useState, useEffect } from "react";
import { useAuth } from "../Context/AuthContext";
import { apiFetch } from "../utils/api";

const ALL_SPORTS = [
  { value: "football",   icon: "\u26BD",        label: "Football" },
  { value: "basketball", icon: "\uD83C\uDFC0",  label: "Basketball" },
  { value: "tennis",     icon: "\uD83C\uDFBE",  label: "Tennis" },
  { value: "cricket",    icon: "\uD83C\uDFCF",  label: "Cricket" },
  { value: "pickleball", icon: "\uD83E\uDD52",  label: "Pickleball" },
  { value: "padel",      icon: "\uD83C\uDFD3",  label: "Padel" },
  { value: "badminton",  icon: "\uD83C\uDFF8",  label: "Badminton" },
];

export default function OnboardingModal() {
  const { user } = useAuth();
  const [show, setShow]       = useState(false);
  const [step, setStep]       = useState(1); // 1=sports, 2=location
  const [sports, setSports]   = useState([]);
  const [city, setCity]       = useState("");
  const [country, setCountry] = useState("");
  const [geoDetected, setGeoDetected] = useState(false);
  const [saving, setSaving]   = useState(false);

  useEffect(function() {
    if (!user) return;
    var token = localStorage.getItem("gt_token") || sessionStorage.getItem("gt_token") || localStorage.getItem("token");
    if (!token) return;
    apiFetch("/api/preferences", { headers: { "Authorization": "Bearer " + token } })
      .then(function(r) { if (!r.ok) return null; return r.json(); })
      .then(function(d) {
        if (!d) return;  // auth failed — do not show modal
        if (!d.onboarding_done) {
          if (d.preferred_sports && d.preferred_sports.length) setSports(d.preferred_sports);
          if (d.city) setCity(d.city);
          if (d.country) setCountry(d.country);
          if (!d.city && !d.country) {
            apiFetch("/api/geoip")
              .then(function(gr) { return gr.json(); })
              .then(function(geo) {
                if (geo.city)    { setCity(geo.city);       setGeoDetected(true); }
                if (geo.country) { setCountry(geo.country); setGeoDetected(true); }
              })
              .catch(function() {});
          }
          setShow(true);
        }
      })
      .catch(function() {});
  }, [user]);

  function toggleSport(val) {
    setSports(function(prev) {
      return prev.includes(val) ? prev.filter(function(s) { return s !== val; }) : prev.concat(val);
    });
  }

  async function save(done) {
    setSaving(true);
    var token = localStorage.getItem("gt_token") || sessionStorage.getItem("gt_token") || localStorage.getItem("token");
    try {
      await apiFetch("/api/preferences", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
        body: JSON.stringify({ preferred_sports: sports, city, country, onboarding_done: done }),
      });
    } catch (e) {}
    setSaving(false);
    if (done) setShow(false);
  }

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative w-full sm:max-w-md bg-zinc-900 border border-zinc-700 rounded-t-2xl sm:rounded-2xl p-6 sm:p-8 flex flex-col gap-5 max-h-[90vh] overflow-y-auto">

        {/* Step 1 — Sports */}
        {step === 1 && (
          <>
            <div>
              <p className="text-zinc-500 text-xs font-mono uppercase tracking-widest mb-1">Welcome to Gametrait</p>
              <h2 className="text-white text-xl font-bold">Which sports?</h2>
              <p className="text-zinc-400 text-sm mt-1">We'll show relevant games and leagues first.</p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {ALL_SPORTS.map(function(s) {
                var sel = sports.includes(s.value);
                return (
                  <button key={s.value} type="button" onClick={function() { toggleSport(s.value); }}
                    className={"flex flex-col items-center gap-1 rounded-xl border py-3 transition-all " +
                      (sel ? "bg-lime-400/10 border-lime-400/50 text-white" : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-500")}>
                    <span className="text-2xl">{s.icon}</span>
                    <span className="text-[10px] font-mono capitalize tracking-wider">{s.label}</span>
                  </button>
                );
              })}
            </div>
            {sports.length > 0 && (
              <p className="text-zinc-500 text-xs text-center -mt-2">
                {sports.length} sport{sports.length !== 1 ? "s" : ""} selected
              </p>
            )}
            <div className="flex gap-3">
              <button type="button" onClick={function() { setStep(2); }}
                className="flex-1 bg-lime-400 hover:bg-lime-300 text-black font-bold rounded-xl py-3 text-sm transition-colors">
                Next {'\u2192'}
              </button>
              <button type="button" onClick={function() { save(true); }}
                className="text-zinc-500 hover:text-zinc-300 text-sm transition-colors px-3">
                Skip
              </button>
            </div>
          </>
        )}

        {/* Step 2 — Location */}
        {step === 2 && (
          <>
            <div>
              <p className="text-zinc-500 text-xs font-mono uppercase tracking-widest mb-1">2 of 2</p>
              <h2 className="text-white text-xl font-bold">Where are you based?</h2>
              <p className="text-zinc-400 text-sm mt-1">We'll show games and leagues near you first.</p>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-zinc-400 text-xs uppercase tracking-widest block mb-1.5">City</label>
                <input type="text" value={city} onChange={function(e) { setCity(e.target.value); setGeoDetected(false); }}
                  placeholder="e.g. Nagpur, London, Mumbai" autoFocus
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2.5 text-white text-sm placeholder-zinc-500 focus:outline-none focus:border-lime-400 transition-colors" />
              </div>
              <div>
                <label className="text-zinc-400 text-xs uppercase tracking-widest block mb-1.5">Country</label>
                <input type="text" value={country} onChange={function(e) { setCountry(e.target.value); setGeoDetected(false); }}
                  placeholder="e.g. India, UK, USA"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2.5 text-white text-sm placeholder-zinc-500 focus:outline-none focus:border-lime-400 transition-colors" />
              </div>
            </div>
            <div className="flex gap-3 items-center">
              {geoDetected && (
                <p className="text-lime-600 text-[11px] flex items-center gap-1 -mt-1">
                  Auto-detected from your IP · edit if incorrect
                </p>
              )}
              <button type="button" onClick={function() { setStep(1); }}
                className="text-zinc-500 hover:text-zinc-300 text-sm transition-colors px-3">
                {'\u2190'} Back
              </button>
              <button type="button" onClick={function() { save(true); }} disabled={saving}
                className="flex-1 bg-lime-400 hover:bg-lime-300 disabled:opacity-50 text-black font-bold rounded-xl py-3 text-sm transition-colors">
                {saving ? "Saving\u2026" : "Done"}
              </button>
            </div>
          </>
        )}

      </div>
    </div>
  );
}